<script lang="ts">
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { usePlansStore } from '@/stores/plans'
import type { Benefit } from '@/types'
import { toPeriod } from '@/utils/date-time-utils'
import { capitalCase } from 'change-case'
import { type PropType, defineComponent } from 'vue'

/**
 * A modal to end the user's current trial (which will immediately charge the user).
 */
export default defineComponent({
  props: {
    /**
     * A configured benefit object.
     */
    benefit: {
      type: Object as PropType<Benefit>,
      required: true,
    },
  },
  setup() {
    return {
      plansStore: usePlansStore(),
      routeMeta: useRouteMeta().meta,
      toPeriod,
    }
  },
  data() {
    return {
      isLoading: false,
    }
  },
  computed: {
    benefitTitle(): string {
      return (
        this.benefit.title +
        (['tickets', 'movies'].includes(this.benefit.redemption_category)
          ? ` ${capitalCase(this.benefit.redemption_category)}`
          : '')
      )
    },
  },
  methods: {
    async endTrial() {
      try {
        this.isLoading = true

        const success = await this.plansStore.endTrial()

        if (success) {
          try {
            await this.$userStore.waitForTrialEnd()
          } catch (error) {
            this.$alertStore.addErrorAlert({
              message:
                'Your trial is processing. We will refresh your account shortly.',
            })
            return
          }

          const planName = this.$userStore.highestPlan?.name ?? 'your plan'

          this.$alertStore.addSuccessAlert({
            title: 'Trial ended',
            message: `You're now on ${planName}.`,
            buttonText: 'Continue',
          })
        }
      } catch (e: any) {
        this.$alertStore.addErrorAlert({
          message: e.error?.message ?? e.message,
        })
      } finally {
        this.isLoading = false
      }
    },
  },
})
</script>

<template>
  <AppModal
    id="end-trial-modal"
    close-button-class="right-5 top-5 bg-pure-white"
    class="max-w-screen-sm"
  >
    <template #button="{ openModal }">
      <slot :open-modal>
        <AppButton
          class="button-primary self-center w-full"
          title="End Trial to Claim"
          @click="openModal"
        >
          End Trial to Claim
        </AppButton>
      </slot>
    </template>

    <template #default="{ closeModal }">
      <div
        class="end-trial-modal-content p-4 md:p-6 flex flex-col justify-center gap-6"
      >
        <p class="font-bold capitalize text-2xl/8">
          Redeem {{ benefit.title }}
        </p>

        <img
          :src="benefit.image"
          :title="benefit.title"
          class="mx-auto aspect-4/5 max-w-[50%] rounded-lg object-cover"
        />

        <p class="md:text-xl/6">
          In order to use this benefit, you need to end your trial.
          <br />
          You will be charged:
          <strong>
            {{
              $n($userStore.highestPlan?.discount_cost ?? 0, 'currency', {
                minimumFractionDigits: 2,
              })
            }}
          </strong>
        </p>

        <div>
          <AppButton
            title="Confirm End Trial"
            :is-loading="isLoading"
            class="button-primary mx-auto w-full font-bold leading-none"
            :icon="Icons.ArrowRight"
            @click="endTrial"
          >
            Confirm End Trial
          </AppButton>

          <button
            type="button"
            class="mx-auto w-full underline hover:no-underline mt-3"
            title="No thanks"
            @click="closeModal"
          >
            No thanks
          </button>
        </div>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
.end-trial-modal-content {
  min-height: fit-content;
  height: auto;
  // Safari iOS
  -webkit-overflow-scrolling: touch;
}
</style>
